/**
 * The camera, and what comes off it.
 *
 * Three jobs, all of them on the device and none of them sending a
 * picture anywhere:
 *
 *   1. Enrolment and re-authentication. Runs the movement challenge from
 *      packages/face - the same liveness pipeline v1 uses - and turns the
 *      averaged embedding into a cancelable face code under this
 *      deployment's pepper.
 *   2. Reading the face off an identity document, the same way, so the
 *      two can be compared by Hamming distance on the server.
 *   3. Judging whether a photograph of a document is good enough to
 *      bother uploading, so somebody on a metered connection is told
 *      "it is out of focus" before they spend the data rather than after.
 *
 * **Nothing here transmits an image, a video frame or an embedding.** The
 * only things that leave are the 48-byte code, the 32 band hashes, and a
 * handful of quality numbers - and, for a KYC case specifically, the
 * document photograph itself, which the customer is explicitly asked for
 * and which is sealed before it is stored.
 */

import { FaceScanner, type ScanResult, type ChallengeStep, type FaceConfig, ACTION_PROMPTS } from '../../../packages/face/src/face.ts';
import { encodeFaceCode, bandHashes, l2normalize } from '../../../packages/protocol/src/identity.ts';
import { toHex } from '../../../packages/protocol/src/bytes.ts';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare const faceapi: any;

/**
 * The pepper this deployment's face codes are computed under.
 *
 * Public by necessity - every client must use the same one or no two
 * codes are comparable - and deliberately different from the v1 mainnet
 * pepper, so a code enrolled here cannot be cross-referenced against the
 * v1 ledger's identity index. That separation is the whole point of a
 * cancelable biometric: the same face produces unrelated codes in two
 * systems that do not share a pepper.
 */
export let FACE_PEPPER = 'facewallet/v2/face/lsh/v1';

export function setFacePepper(pepper: string | undefined): void {
  if (pepper && pepper.length > 4) FACE_PEPPER = pepper;
}

/**
 * Whether the movement challenge runs.
 *
 * Set from the deployment, never guessed. With it off, a face is
 * captured and nothing establishes that a living person produced it -
 * a photograph held to the lens passes - so the wallet says so on the
 * screen rather than skipping the step quietly.
 */
export let LIVENESS_MODE: 'movement' | 'off' = 'movement';

export function setLivenessMode(mode: string | undefined): void {
  LIVENESS_MODE = mode === 'off' ? 'off' : 'movement';
}

export const livenessIsOff = (): boolean => LIVENESS_MODE === 'off';

export interface FaceCode {
  code: string;
  bands: string[];
}

/** Turn an embedding into what the server is allowed to see. */
export function codeFrom(embedding: ArrayLike<number>): FaceCode {
  const bytes = encodeFaceCode(embedding, FACE_PEPPER);
  return { code: toHex(bytes), bands: bandHashes(bytes, FACE_PEPPER) };
}

/* ------------------------------ enrolment ------------------------------- */

export interface ScanOutcome {
  ok: boolean;
  reason?: string;
  code?: string;
  bands?: string[];
  liveness?: Record<string, unknown>;
}

export interface ScanUi {
  video: HTMLVideoElement;
  overlay: HTMLCanvasElement;
  prompt: (text: string) => void;
  step?: (step: ChallengeStep) => void;
}

/**
 * How this deployment tunes the scanner.
 *
 * Every check the pipeline makes is still made. What changes is how hard
 * it is to be *seen* at all, and the answer is calibrated for the phone
 * this will actually run on: a mid-range Android with a mediocre front
 * camera, indoors, often at night, held by somebody wearing glasses.
 *
 * The detector confidence is the important one. At the library default a
 * person looking straight at the lens in a dim room is frequently not
 * detected, and the pipeline then tells them there is no face in the
 * picture - which reads as the product calling them a liar. Lowering it
 * costs nothing in security: a false detection produces a face code that
 * simply will not match anything, and the movement, jaw-geometry and
 * descriptor-continuity checks are unchanged.
 */
const V2_SCANNER: Partial<FaceConfig> = {
  detectConfidence: 0.32,
  minDetectionScore: 0.32,
  fastConfidence: 0.3,
  acquireMs: 10_000,
  // Forgiving of a small movement, and generous about how long it takes.
  // Someone unfamiliar with the gesture, holding a phone at arm's length,
  // takes a moment - and being told they did not move when they did is
  // the failure that makes people give up.
  yawDelta: 0.13,
  pitchDelta: 0.085,
  smileDelta: 0.045,
  minAsym: 0.018,
  stepTimeoutMs: 12_000,
  settleMs: 900,
  framesPerScan: 8,
};

let scanner: FaceScanner | null = null;

export async function openCamera(ui: ScanUi): Promise<void> {
  if (!scanner) scanner = new FaceScanner(ui.video, ui.overlay, V2_SCANNER);
  if (scanner.isReady) return;
  await scanner.init((stage, detail) => {
    ui.prompt(
      stage === 'models' ? 'Getting ready…'
      : stage === 'camera' ? 'Allow the camera to continue'
      : detail,
    );
  });
}

export function closeCamera(): void {
  scanner?.stop();
  scanner = null;
}

/**
 * Run the movement challenge the server issued and produce a face code.
 *
 * The sequence comes from the server and did not exist before this
 * request, so a recording made earlier contains the wrong movements. The
 * scanner also checks that the jaw geometry changed the way a solid head
 * does and that the same face is present at both ends of the sequence.
 */
export async function runChallenge(ui: ScanUi, sequence: string): Promise<ScanOutcome> {
  await openCamera(ui);
  if (!scanner) return { ok: false, reason: 'The camera did not start.' };

  // With liveness off there is no sequence to walk: the frames are
  // averaged and the code produced from them, exactly as the last step
  // of a full scan would.
  if (LIVENESS_MODE === 'off' || !sequence) {
    const captured = await scanner.capture((_stage, detail) => ui.prompt(detail));
    if (!captured.ok || !captured.embedding) {
      return { ok: false, reason: captured.reason ?? 'The face could not be captured.' };
    }
    const { code, bands } = codeFrom(captured.embedding);
    return { ok: true, code, bands, liveness: { ...captured.metrics } };
  }

  const result: ScanResult = await scanner.scan(
    sequence,
    (_stage, detail) => ui.prompt(detail),
    (step) => {
      ui.step?.(step);
      ui.prompt(ACTION_PROMPTS[step.action] ?? 'Follow the prompt');
    },
  );

  if (!result.ok || !result.embedding) return { ok: false, reason: result.reason ?? 'The face check did not pass.' };

  // The scanner computes a code under the v1 pepper; recompute under this
  // deployment's own. The embedding never leaves this function.
  const { code, bands } = codeFrom(result.embedding);
  return { ok: true, code, bands, liveness: { ...result.metrics } };
}

/** A quicker check for signing in or approving a payment. */
export async function quickFaceCode(ui: ScanUi): Promise<ScanOutcome> {
  await openCamera(ui);
  if (!scanner) return { ok: false, reason: 'The camera did not start.' };
  if (LIVENESS_MODE === 'off') return runChallenge(ui, '');
  // One movement rather than a full sequence. Enough to defeat a held-up
  // photograph, fast enough to use at a till with somebody waiting.
  const sequence = 'LRUDS'[Math.floor(Math.random() * 5)];
  return runChallenge(ui, sequence);
}

export const isCameraReady = (): boolean => scanner?.isReady ?? false;

/* -------------------------- documents and quality ------------------------ */

export interface ImageQuality {
  width: number;
  height: number;
  sharpness: number;
  brightness: number;
  glare: number;
  facesDetected: number;
}

export interface DocumentRead {
  quality: ImageQuality;
  /** The face code from the photograph on the document, if one was found. */
  faceCode?: string;
  /** Base64 of the resized image actually uploaded. */
  imageBase64: string;
  mime: string;
}

/** Longest edge of an uploaded document photo. Beyond this adds bytes, not detail. */
const MAX_EDGE = 1600;

async function toCanvas(file: Blob): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser cannot process the image.');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  return canvas;
}

/**
 * Measure the photograph.
 *
 * Sharpness is the variance of a Laplacian over the luminance channel -
 * the standard cheap focus measure, and reliable enough to separate "out
 * of focus" from "fine" without a model. Glare is the fraction of pixels
 * at or near saturation, which is what a flash on a laminated card
 * produces and what destroys the printed detail underneath.
 */
export function measure(canvas: HTMLCanvasElement, facesDetected: number): ImageQuality {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser cannot process the image.');
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;

  const luma = new Float32Array(width * height);
  let sum = 0;
  let bright = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    luma[p] = y;
    sum += y;
    if (y > 245) bright++;
  }
  const mean = sum / luma.length;

  // Sample the Laplacian on a grid rather than every pixel: the variance
  // is the same to two significant figures and this runs in a few
  // milliseconds on a cheap phone rather than a few hundred.
  let lapSum = 0;
  let lapSq = 0;
  let n = 0;
  const step = Math.max(1, Math.floor(Math.min(width, height) / 300));
  for (let y = step; y < height - step; y += step) {
    for (let x = step; x < width - step; x += step) {
      const i = y * width + x;
      const v = 4 * luma[i] - luma[i - 1] - luma[i + 1] - luma[i - width] - luma[i + width];
      lapSum += v;
      lapSq += v * v;
      n++;
    }
  }
  const lapMean = n ? lapSum / n : 0;
  const variance = n ? lapSq / n - lapMean * lapMean : 0;

  return {
    width,
    height,
    sharpness: Math.round(variance * 100) / 100,
    brightness: Math.round(mean * 10) / 10,
    glare: Math.round((bright / luma.length) * 10_000) / 10_000,
    facesDetected,
  };
}

/**
 * Read an identity document.
 *
 * Finds the face on the card, turns it into a code under the same pepper
 * the enrolment used, and measures the photograph. The comparison itself
 * happens on the server against the enrolled code, which the browser has
 * never seen - so a client cannot simply assert that the faces matched.
 */
export async function readDocument(file: Blob, opts: { needsFace?: boolean } = {}): Promise<DocumentRead> {
  const canvas = await toCanvas(file);
  let faceCode: string | undefined;
  let facesDetected = 0;

  if (opts.needsFace !== false && typeof faceapi !== 'undefined') {
    // A printed passport photograph is small, low contrast and often
    // half-toned, so the threshold is lower than for a live camera frame.
    // Missing the face entirely refers the case to a human; a false
    // detection is caught by the code comparison that follows.
    const detections = await faceapi
      .detectAllFaces(canvas, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.28 }))
      .withFaceLandmarks()
      .withFaceDescriptors();
    facesDetected = detections.length;
    if (detections.length > 0) {
      // The largest face on the card is the holder. A secondary ghost
      // portrait, where one is printed, is smaller by design.
      const best = detections.reduce((a: any, b: any) =>
        a.detection.box.area >= b.detection.box.area ? a : b);
      faceCode = codeFrom(l2normalize(Array.from(best.descriptor as Float32Array))).code;
    }
  }

  const quality = measure(canvas, facesDetected);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
  if (!blob) throw new Error('The image could not be prepared.');

  return {
    quality,
    faceCode,
    imageBase64: await toBase64(blob),
    mime: 'image/jpeg',
  };
}

async function toBase64(blob: Blob): Promise<string> {
  const buffer = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  // Chunked, because String.fromCharCode with a few hundred thousand
  // arguments overflows the call stack on some engines.
  for (let i = 0; i < buffer.length; i += 8192) {
    binary += String.fromCharCode(...buffer.subarray(i, i + 8192));
  }
  return btoa(binary);
}

/** Ensure the face models are loaded, for a document read with no camera. */
export async function ensureModels(): Promise<void> {
  if (typeof faceapi === 'undefined') throw new Error('The face engine did not load. Check your connection and reload.');
  if (faceapi.nets.ssdMobilenetv1.isLoaded && faceapi.nets.faceRecognitionNet.isLoaded) return;
  const { DEFAULT_FACE_CONFIG } = await import('../../../packages/face/src/face.ts');
  const { enforceModelIntegrity } = await import('../../../packages/face/src/models.ts');
  enforceModelIntegrity(DEFAULT_FACE_CONFIG.modelUrl);
  await Promise.all([
    faceapi.nets.ssdMobilenetv1.loadFromUri(DEFAULT_FACE_CONFIG.modelUrl),
    faceapi.nets.faceLandmark68Net.loadFromUri(DEFAULT_FACE_CONFIG.modelUrl),
    faceapi.nets.faceRecognitionNet.loadFromUri(DEFAULT_FACE_CONFIG.modelUrl),
  ]);
}

export { ACTION_PROMPTS };
export type { ChallengeStep };
